import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Grid,
  useTheme,
  useMediaQuery,
  Skeleton,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import axios from "axios";
import { API_URL } from "../../consts";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { storeProfileSafely } from "../../utils/authUtils";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)",
  backdropFilter: "blur(10px)",
  borderRadius: "16px",
  border: "1px solid rgba(226,232,240,0.8)",
  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
  overflow: "hidden",
}));

const StyledTable = styled(Table)(({ theme }) => ({
  "& .MuiTableHead-root": {
    background: "linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)",
  },
  "& .MuiTableCell-head": {
    fontWeight: 700,
    fontSize: "14px",
    color: "rgba(30,41,59,0.9)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid rgba(99,102,241,0.2)",
    padding: theme.spacing(2),
  },
  "& .MuiTableCell-body": {
    borderBottom: "1px solid rgba(226,232,240,0.6)",
    padding: theme.spacing(2),
  },
  "& .MuiTableRow-root": {
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)",
      transform: "translateX(4px)",
    },
    "&:last-child .MuiTableCell-body": {
      borderBottom: "none",
    },
  },
}));

const RankCell = styled(Box)(({ theme, rank }) => {
  const getRankColor = (rank) => {
    if (rank === 1) return "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)";
    if (rank === 2) return "linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)";
    if (rank === 3) return "linear-gradient(135deg, #CD7F32 0%, #B87333 100%)";
    return "linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)";
  };

  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: getRankColor(rank),
    color: rank <= 3 ? "#fff" : "rgba(71,85,105,0.8)",
    fontWeight: 700,
    fontSize: "16px",
    boxShadow:
      rank <= 3
        ? "0 4px 12px rgba(0,0,0,0.2)"
        : "0 2px 8px rgba(99,102,241,0.15)",
  };
});

const PointsChip = styled(Chip)(({ theme }) => ({
  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
  color: "#fff",
  fontWeight: 600,
  fontSize: "13px",
  height: "28px",
  boxShadow: "0 2px 8px rgba(99,102,241,0.3)",
  "& .MuiChip-label": {
    padding: "0 12px",
  },
}));

const TopUsers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.token);
  const userData = useSelector((state) => state.userData);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [error, setError] = useState(null);
  const [allowToShow, setAllowToShow] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUserClick = (user) => {
    // Navigate to user profile using user_name (or fallback to id for backward compatibility)
    if (user.user_name) {
      navigate(`/user/${user.user_name}`);
    } else if (user.id) {
      // Fallback to id for backward compatibility
      navigate(`/user/${user.id}`);
    }
  };

  useEffect(() => {
    fetchTopUsers();
    if (token) {
      fetchUserProfile();
    }
  }, [token]);

  // Update toggle when userData changes (from Redux or localStorage)
  useEffect(() => {
    if (userData) {
      // Check if allow_to_show_in_top_users exists in userData
      // If it's explicitly false, use false; otherwise default to true
      const shouldShow = userData.allow_to_show_in_top_users !== false;
      setAllowToShow(shouldShow);
    }
  }, [userData]);

  const fetchUserProfile = async () => {
    if (!token) return;
    
    try {
      // SECURITY: Cookies are sent automatically, no Authorization header needed
      const response = await axios.get(`${API_URL}/profile`, {
        withCredentials: true,
      });
      
      if (response.data && response.data.profile) {
        const profile = response.data.profile;
        // The backend now always includes allow_to_show_in_top_users (defaults to True)
        // Use the value from the profile, defaulting to true if not present
        const shouldShow = profile.allow_to_show_in_top_users !== false;
        setAllowToShow(shouldShow);
        
        // Update Redux if the profile has the field and it's different
        if (profile.allow_to_show_in_top_users !== undefined) {
          dispatch({ type: "SET_USER_DATA", payload: profile });
          // Store only non-sensitive profile data
          storeProfileSafely(profile);
        }
      }
    } catch (err) {
      console.error("Error fetching user profile:", err);
      // Default to true on error
      setAllowToShow(true);
    }
  };

  const handleToggleVisibility = async (event) => {
    const newValue = event.target.checked;
    setAllowToShow(newValue);
    setIsUpdating(true);
    
    try {
      const response = await axios.put(
        `${API_URL}/profile`,
        {
          allow_to_show_in_top_users: newValue,
        },
        {
          withCredentials: true, // SECURITY: Cookies are sent automatically
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      
      // Update user data in Redux
      if (response.data && response.data.profile) {
        dispatch({ type: "SET_USER_DATA", payload: response.data.profile });
        // Store only non-sensitive profile data
        storeProfileSafely(response.data.profile);
      }
      
      // Refresh the leaderboard to reflect the change
      await fetchTopUsers();
      
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: newValue 
            ? "You are now visible on the leaderboard" 
            : "You are now hidden from the leaderboard",
          type: "success",
        },
      });
      setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), 3000);
    } catch (err) {
      console.error("Error updating visibility:", err);
      // Revert on error
      setAllowToShow(!newValue);
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: err.response?.data?.error || "Failed to update visibility setting",
          type: "error",
        },
      });
      setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), 3000);
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchTopUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // SECURITY: Cookies are sent automatically, no Authorization header needed
      const response = await axios.get(`${API_URL}/points/top-users?limit=50`, {
        withCredentials: true,
      });
      if (response.data) {
        if (response.data.users) {
          setUsers(response.data.users);
        }
        if (response.data.user_rank !== undefined) {
          setUserRank(response.data.user_rank);
        }
      }
    } catch (err) {
      console.error("Error fetching top users:", err);
      setError("Failed to load top users. Please try again later.");
      if (err.response?.status === 401) {
        dispatch({ type: "SET_UNAUTHORIZED" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getColorForUser = (name) => {
    const colors = [
      "#6366f1", // Indigo
      "#8b5cf6", // Violet
      "#06b6d4", // Cyan
      "#10b981", // Emerald
      "#f59e0b", // Amber
      "#ef4444", // Red
    ];
    const firstLetter = name?.[0] || "A";
    const colorIndex = firstLetter.charCodeAt(0) % colors.length;
    return colors[colorIndex];
  };

  const formatPoints = (points) => {
    if (points >= 1000) {
      return `${(points / 1000).toFixed(1)}k`;
    }
    return points.toString();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.9) 100%)",
        pt: { xs: 2, sm: 4, md: 6 },
        px: { xs: 1, sm: 3, md: 4 },
        pb: { xs: 8, sm: 4 },
      }}
    >
      <Box sx={{ maxWidth: "1400px", mx: "auto" }}>
        {/* Header */}
        <Box
          sx={{
            mb: { xs: 2, sm: 3, md: 4 },
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: { xs: "48px", sm: "64px" },
              height: { xs: "48px", sm: "64px" },
              borderRadius: "16px",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              boxShadow: "0 4px 16px rgba(99,102,241,0.3)",
            }}
          >
            <EmojiEventsIcon
              sx={{
                fontSize: { xs: "28px", sm: "36px" },
                color: "#fff",
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                background: "linear-gradient(135deg, #1a202c 0%, #6366f1 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                mb: 0.5,
              }}
            >
              Top 50 Users
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(71,85,105,0.7)",
                fontSize: { xs: "13px", sm: "15px" },
              }}
            >
              Discover the most active contributors in our community
            </Typography>
          </Box>
        </Box>

        {/* User Rank Display and Visibility Toggle */}
        {token && (
          <Box
            sx={{
              mb: { xs: 2, sm: 3 },
              p: 2,
              borderRadius: "12px",
              background: "linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)",
              border: "1px solid rgba(99,102,241,0.2)",
            }}
          >
            {userRank !== null && userRank !== undefined && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                }}
              >
                <EmojiEventsIcon
                  sx={{
                    fontSize: { xs: "24px", sm: "28px" },
                    color: "#6366f1",
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "14px", sm: "16px" },
                      color: "rgba(30,41,59,0.9)",
                      mb: 0.5,
                    }}
                  >
                    Your Global Rank
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "18px", sm: "24px" },
                      fontWeight: 700,
                      background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    #{userRank}
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Visibility Toggle */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: "8px",
                background: "rgba(255,255,255,0.5)",
              }}
            >
              {allowToShow ? (
                <VisibilityIcon
                  sx={{
                    fontSize: "20px",
                    color: "#6366f1",
                  }}
                />
              ) : (
                <VisibilityOffIcon
                  sx={{
                    fontSize: "20px",
                    color: "rgba(71,85,105,0.6)",
                  }}
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={allowToShow}
                    onChange={handleToggleVisibility}
                    disabled={isUpdating}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#6366f1",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#6366f1",
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: { xs: "13px", sm: "14px" },
                      fontWeight: 500,
                      color: "rgba(30,41,59,0.9)",
                    }}
                  >
                    {allowToShow ? "Visible on leaderboard" : "Hidden from leaderboard"}
                  </Typography>
                }
                sx={{ margin: 0 }}
              />
            </Box>
          </Box>
        )}

        {/* Error Message */}
        {error && (
          <Box
            sx={{
              p: 3,
              mb: 3,
              borderRadius: "12px",
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.3)",
            }}
          >
            <Typography
              sx={{
                color: "rgba(239,68,68,0.9)",
                fontWeight: 500,
              }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {/* Table Container */}
        {isLoading ? (
          <Box sx={{ p: 3 }}>
            {[...Array(5)].map((_, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Skeleton
                  variant="rectangular"
                  height={80}
                  sx={{
                    borderRadius: "12px",
                    background:
                      "linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)",
                  }}
                />
              </Box>
            ))}
          </Box>
        ) : users.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: "12px",
              background: "rgba(71,85,105,0.05)",
            }}
          >
            <Typography
              sx={{
                color: "rgba(71,85,105,0.7)",
                fontSize: "16px",
              }}
            >
              No users found
            </Typography>
          </Box>
        ) : (
          <StyledTableContainer component={Paper}>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ width: "80px" }}>
                    Rank
                  </TableCell>
                  <TableCell>User</TableCell>
                  <TableCell align="center" sx={{ minWidth: "120px" }}>
                    Points
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: "150px" }}>
                    Title
                  </TableCell>
                  <TableCell>Areas of Interest</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, index) => {
                  const rank = index + 1;
                  const color = getColorForUser(user.name);
                  return (
                    <TableRow key={user.user_name || user.id || index}>
                      <TableCell align="center">
                        <RankCell rank={rank}>
                          {rank <= 3 ? (
                            <EmojiEventsIcon
                              sx={{ fontSize: "20px", color: "#fff" }}
                            />
                          ) : (
                            rank
                          )}
                        </RankCell>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            cursor: "pointer",
                            "&:hover": {
                              opacity: 0.8,
                            },
                          }}
                          onClick={() => handleUserClick(user)}
                        >
                          {user.profile_image_url ? (
                            <Avatar
                              src={user.profile_image_url}
                              alt={user.name}
                              sx={{
                                width: { xs: 40, sm: 48 },
                                height: { xs: 40, sm: 48 },
                                border: `2px solid ${color}40`,
                                boxShadow: `0 2px 8px ${color}30`,
                                cursor: "pointer",
                              }}
                            />
                          ) : (
                            <Avatar
                              sx={{
                                width: { xs: 40, sm: 48 },
                                height: { xs: 40, sm: 48 },
                                background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                                color: "#fff",
                                fontSize: { xs: "16px", sm: "20px" },
                                fontWeight: "bold",
                                boxShadow: `0 2px 8px ${color}30`,
                                cursor: "pointer",
                              }}
                            >
                              {user.name?.[0]?.toUpperCase() || "?"}
                            </Avatar>
                          )}
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: "14px", sm: "16px" },
                              color: "rgba(30,41,59,0.9)",
                              cursor: "pointer",
                              "&:hover": {
                                color: "#6366f1",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {user.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <PointsChip
                          label={formatPoints(user.points)}
                          icon={
                            <EmojiEventsIcon
                              sx={{ fontSize: "16px", color: "#fff" }}
                            />
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        {user.title && user.title.trim() && (
                          <Chip
                            label={user.title}
                            sx={{
                              background: "rgba(99,102,241,0.1)",
                              color: "rgba(99,102,241,0.9)",
                              fontWeight: 500,
                              fontSize: "13px",
                              height: "28px",
                              border: "1px solid rgba(99,102,241,0.2)",
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.75,
                          }}
                        >
                          {user.most_interested && user.most_interested.length > 0 ? (
                            user.most_interested.map((interest, idx) => (
                              <Chip
                                key={idx}
                                label={interest}
                                size="small"
                                sx={{
                                  background: "rgba(139,92,246,0.1)",
                                  color: "rgba(139,92,246,0.9)",
                                  fontWeight: 500,
                                  fontSize: "12px",
                                  height: "24px",
                                  border: "1px solid rgba(139,92,246,0.2)",
                                  "&:hover": {
                                    background: "rgba(139,92,246,0.15)",
                                  },
                                }}
                              />
                            ))
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "rgba(71,85,105,0.5)",
                                fontStyle: "italic",
                              }}
                            >
                              No interests specified
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </StyledTable>
          </StyledTableContainer>
        )}

        {/* Mobile Card View (Alternative for very small screens) */}
        {isMobile && (
          <Box sx={{ mt: 3, display: { xs: "block", sm: "none" } }}>
            {users.map((user, index) => {
              const rank = index + 1;
              const color = getColorForUser(user.name);
              return (
                <Paper
                  key={user.user_name || user.id || index}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: "12px",
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)",
                    border: "1px solid rgba(226,232,240,0.8)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <RankCell rank={rank}>
                      {rank <= 3 ? (
                        <EmojiEventsIcon
                          sx={{ fontSize: "18px", color: "#fff" }}
                        />
                      ) : (
                        rank
                      )}
                    </RankCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flex: 1,
                        cursor: "pointer",
                        "&:hover": {
                          opacity: 0.8,
                        },
                      }}
                      onClick={() => handleUserClick(user)}
                    >
                      {user.profile_image_url ? (
                        <Avatar
                          src={user.profile_image_url}
                          alt={user.name}
                          sx={{
                            width: 48,
                            height: 48,
                            border: `2px solid ${color}40`,
                            cursor: "pointer",
                          }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                            color: "#fff",
                            fontSize: "20px",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                        >
                          {user.name?.[0]?.toUpperCase() || "?"}
                        </Avatar>
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            fontSize: "16px",
                            color: "rgba(30,41,59,0.9)",
                            mb: user.title && user.title.trim() ? 0.5 : 0,
                            cursor: "pointer",
                            "&:hover": {
                              color: "#6366f1",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {user.name}
                        </Typography>
                        {user.title && user.title.trim() && (
                          <Chip
                            label={user.title}
                            size="small"
                            sx={{
                              background: "rgba(99,102,241,0.1)",
                              color: "rgba(99,102,241,0.9)",
                              fontWeight: 500,
                              fontSize: "12px",
                              height: "24px",
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    <PointsChip
                      label={formatPoints(user.points)}
                      icon={
                        <EmojiEventsIcon
                          sx={{ fontSize: "14px", color: "#fff" }}
                        />
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.75,
                    }}
                  >
                    {user.most_interested && user.most_interested.length > 0 ? (
                      user.most_interested.map((interest, idx) => (
                        <Chip
                          key={idx}
                          label={interest}
                          size="small"
                          sx={{
                            background: "rgba(139,92,246,0.1)",
                            color: "rgba(139,92,246,0.9)",
                            fontWeight: 500,
                            fontSize: "11px",
                            height: "22px",
                          }}
                        />
                      ))
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(71,85,105,0.5)",
                          fontStyle: "italic",
                        }}
                      >
                        No interests specified
                      </Typography>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TopUsers;
